import {
  DiagramProps,
  DiagramLayoutOutput,
  DiagramLinkData,
  DiagramNodeData,
  State
} from './diagram.types';
import {
  SelectedItemDetail,
  ItemHoverDetail,
  ItemFocusDetail,
  CommonDiagramProps
} from '#PRIVATE_BaseDiagram/diagram.types';
import { BaseDiagram } from '#PRIVATE_BaseDiagram';
import { ComponentChildren } from 'preact';

// Re-alias or restrict as needed; you might not need to define new types
export type FlatDiagramProps<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
> = DiagramProps<K1, K2, D1, D2, L>;

/**
 * Diagrams are used to display a set of nodes and the links between them.
 */
export function Diagram<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1>,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>(props: FlatDiagramProps<K1, K2, D1, D2, L>) {
  // Adapt or map only as needed. For a flat diagram:
  const {
    selectedIds,
    highlightedIds,
    onSelectionChange,
    onItemFocus,
    onItemHover,
    nodeRenderer,
    ...rest // all other props
  } = props;

  // Map selectedIds/highlightedIds to match BaseDiagram's prop signature
  const mappedSelectedIds = selectedIds && {
    nodes: selectedIds.nodes,
    links: selectedIds.links
    // NO promotedLinks for flat diagrams
  };
  const mappedHighlightedIds = highlightedIds && {
    nodes: highlightedIds.nodes,
    links: highlightedIds.links
    // NO promotedLinks for flat diagrams
  };

  // Map event handlers to match BaseDiagram's more complex handler signatures, if needed
  const mappedOnSelectionChange = onSelectionChange
    ? (detail: SelectedItemDetail<K1, K2, D1, D2>) => {
        // BaseDiagram's detail = { nodes: {id, data}, links: {id, data}, promotedLinks: {id[]} }
        // FlatDiagram expects { nodes: K1[], links: K2[], nodeData: D1[], linkData: D2[] }
        onSelectionChange({
          nodes: {
            ids: detail.nodes?.ids ?? [],
            data: detail.nodes?.data ?? []
          },
          links: {
            ids: detail.links?.ids ?? [],
            data: detail.links?.data ?? []
          }
        });
      }
    : undefined;

  const mappedOnItemHover = onItemHover
    ? (detail: ItemHoverDetail<K1, K2, D1, D2>) => {
        // Ignore promotedLink case for FlatDiagram
        if (!detail || (detail && (detail.type === 'node' || detail.type === 'link'))) {
          onItemHover(detail);
        }
      }
    : undefined;

  const mappedOnItemFocus = onItemFocus
    ? (detail: ItemFocusDetail<K1, K2, D1, D2>) => {
        // Ignore promotedLink case for FlatDiagram
        if (!detail || (detail && (detail.type === 'node' || detail.type === 'link'))) {
          onItemFocus(detail);
        }
      }
    : undefined;

  const baseDiagramNodeRenderer = (ctx: { data: D1; state: State; expander: ComponentChildren }) =>
    nodeRenderer({
      data: ctx.data,
      state: ctx.state
      // expander is ignored by flat nodeRenderer
    });

  return (
    <BaseDiagram<K1, K2, D1, D2, L>
      {...(rest as Omit<
        CommonDiagramProps<K1, K2, D1, D2, L>,
        | 'selectedIds'
        | 'highlightedIds'
        | 'onSelectionChange'
        | 'onItemHover'
        | 'onItemFocus'
        | 'expanded'
        | 'onExpandedChange'
        | 'nodeRenderer'
      >)}
      selectedIds={mappedSelectedIds}
      highlightedIds={mappedHighlightedIds}
      onSelectionChange={mappedOnSelectionChange}
      onItemFocus={mappedOnItemFocus}
      onItemHover={mappedOnItemHover}
      nodeRenderer={baseDiagramNodeRenderer}
      normalizeLinks={true}
    />
  );
}
